/** @jsx jsx */
import type { ReactNode, Ref } from 'react';
import { useMemo } from 'react';
import { useCallback } from 'react';
import type { DataAttributeMap } from '@balance-web/core';
import { buildDataAttributes, jsx } from '@balance-web/core';
import type { ButtonProps, ButtonStylesProps } from '@balance-web/button';
import { Button, getButtonStyles } from '@balance-web/button';
import { Dropdown } from '@balance-web/dropdown-v2';
import { Flex } from '@balance-web/flex';
import { useRawTheme, useTheme } from '@balance-web/theme';
import { ChevronDownIcon } from '@balance-web/icon/icons/ChevronDownIcon';

type Action = Omit<
  ButtonProps,
  'iconAfter' | 'colorScheme' | 'variant' | 'size' | 'disabled' | 'loading'
> & {
  onClick: () => void;
  primary?: boolean;
};

export type SplitButtonProps = {
  buttonRef?: Ref<HTMLButtonElement>;
  button: Action;
  disabled?: boolean;
  menuItems: ReactNode;
  buttonData?: DataAttributeMap;
  menuTriggerData?: DataAttributeMap;
} & Pick<ButtonProps, 'variant' | 'colorScheme' | 'size' | 'type' | 'loading'>;

export const SplitButton = (props: SplitButtonProps) => {
  const buttonDataAttributes = buildDataAttributes(props.buttonData);
  const menuTriggerDataAttributes = buildDataAttributes(props.menuTriggerData);
  const theme = useTheme();
  const rawTheme = useRawTheme();

  const colorScheme = props.colorScheme || 'primary';
  const variant = props.variant || 'filled';
  const size = props.size || 'medium';
  const loading = props.loading;

  const isClickDisabled = props.loading || props.disabled;
  const isStyledisabled = props.disabled;

  const primaryButtonBorderRadius = `${theme.radii.full} 0px 0px ${theme.radii.full}`;
  /**
   * Do not use this to style the primary button.
   *  It's only used to extract styling from the primary button in order to style
   *  other elements such as the divider.
   */
  const primaryButtonStyles = useMemo(() => {
    const state: ButtonStylesProps = {
      block: props.button.block === true,
      borderRadius: primaryButtonBorderRadius,
      borderRightWidth: 0,
      fontWeight: theme.typography.fontWeight.bold,
      size,
      colorScheme,
      variant,
      disabled: isStyledisabled,
      loading,
      hasIcon: !!props.button.iconBefore,
      isIcon: false,
    };

    return getButtonStyles(state, theme, rawTheme);
  }, [
    colorScheme,
    isStyledisabled,
    loading,
    primaryButtonBorderRadius,
    props.button.block,
    props.button.iconBefore,
    rawTheme,
    size,
    theme,
    variant,
  ]);

  const Divider = useCallback(() => {
    return (
      <div
        css={{
          width: 1,
          backgroundColor: isStyledisabled
            ? theme.primitives.colour.neutral.k80
            : colorScheme === 'secondary'
            ? theme.primitives.colour.primary.k20
            : primaryButtonStyles.root.color,
          opacity:
            colorScheme === 'tertiary' || variant === 'text' || isStyledisabled
              ? 0.3
              : 1,
        }}
      />
    );
  }, [
    isStyledisabled,
    theme.primitives.colour.neutral.k80,
    primaryButtonStyles.root.color,
    colorScheme,
    variant,
  ]);

  if (!props.menuItems) {
    throw Error(
      'SplitButton must have menuItems. Use a Button if there are no menu items available.'
    );
  }

  return (
    <Flex>
      <Button
        ref={props.buttonRef}
        borderRadius={primaryButtonBorderRadius}
        colorScheme={colorScheme}
        disabled={isStyledisabled}
        iconBefore={props.button.iconBefore}
        loading={props.loading}
        size={size}
        variant={variant}
        {...props.button}
        {...buttonDataAttributes}
      />
      <Divider />
      <Dropdown align="end">
        <Dropdown.Trigger disabled={isClickDisabled}>
          {({ isOpen, triggerProps }) => {
            const state: ButtonStylesProps = {
              block: false,
              borderRadius: `0px ${theme.radii.full} ${theme.radii.full} 0px`,
              borderLeftWidth: 0,
              fontWeight: theme.typography.fontWeight.bold,
              size,
              colorScheme,
              variant,
              disabled: isStyledisabled,
              hasIcon: false,
              isIcon: false,
            };

            const dropdownTriggerButtonStyles = getButtonStyles(
              state,
              theme,
              rawTheme
            );

            return (
              <button
                css={dropdownTriggerButtonStyles.root}
                {...triggerProps}
                {...menuTriggerDataAttributes}
                aria-disabled={isClickDisabled}
                aria-label="Toggle menu"
              >
                <ChevronDownIcon
                  css={{ transform: isOpen ? 'rotate(180deg)' : undefined }}
                  size={props.size === 'small' ? 'xsmall' : 'small'}
                />
                <div
                  className="hover-overlay"
                  css={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    pointerEvents: 'none',
                    ...dropdownTriggerButtonStyles.hoverOverlay,
                  }}
                />
                <div
                  className="focused-overlay"
                  css={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    pointerEvents: 'none',
                    ...dropdownTriggerButtonStyles.focusedOverlay,
                  }}
                />
                <div
                  className="pressed-overlay"
                  css={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    pointerEvents: 'none',
                    ...dropdownTriggerButtonStyles.pressedOverlay,
                  }}
                />
              </button>
            );
          }}
        </Dropdown.Trigger>
        {props.menuItems}
      </Dropdown>
    </Flex>
  );
};
